import { Box, Card, Col, FormGroup, FormSection, Row } from '@ksyos/design-system';
import { get } from 'lodash';
import { useId } from 'react';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import type { ValidationErrors } from '@frontend/utils/validation';
import { SelectCountry } from '@frontend/components/elements/SelectCountry';
import { SelectState } from '@frontend/components/elements/SelectState';
import { renderTextInput } from '@frontend/components/pages/organizations/organizationAdd/utils/RenderTextInput';
import { InputText } from '@frontend/components/userDataManagement/InputText';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

type ContactDetailsCardProps = {
    errors: ValidationErrors;
    handleChangeAndValidate: <K extends keyof IOrganization>(name: K, value: IOrganization[K]) => void;
    organization: IOrganization;
};

export function ContactDetailsCard({
    errors,
    handleChangeAndValidate,
    organization,
}: Readonly<ContactDetailsCardProps>) {
    const translate = getSelectedLanguage();
    const houseNumberId = useId();

    function renderTextInputInline(fieldKey: string, label: string, id?: string) {
        return (
            <Box>
                <InputText
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    id={id}
                    nullable={true}
                    placeholder={get(translate, label)}
                />
            </Box>
        );
    }

    function renderHouseNumberFields() {
        return (
            <FormGroup
                hasErrors={errors.has('houseNumber') || errors.has('houseNumberSuffix')}
                htmlFor={houseNumberId}
                label={translate.common.houseNumber}
            >
                <Row columnGap={[2, 4]}>
                    <Col span={6}>{renderTextInputInline('houseNumber', 'common.houseNumber', houseNumberId)}</Col>
                    <Col span={6}>{renderTextInputInline('houseNumberSuffix', 'common.houseNumberSuffix')}</Col>
                </Row>
            </FormGroup>
        );
    }

    return (
        <Card>
            <Card.Header title={translate.organization.contactDetails} />
            <Card.Body>
                <FormSection>
                    {renderTextInput({
                        errors,
                        fieldKey: 'phoneNumber',
                        isRequired: true,
                        label: 'common.phoneNumber',
                    })}
                    {renderTextInput({
                        errors,
                        fieldKey: 'emergencyNumber',
                        isRequired: false,
                        label: 'organization.emergencyNumber',
                        tooltipText: translate.organization.emergencyNumberTooltip,
                    })}
                    {renderTextInput({
                        errors,
                        fieldKey: 'emailAddressRequestInbox',
                        isRequired: true,
                        label: 'common.emailAddress',
                    })}
                    {renderTextInput({ errors, fieldKey: 'url', label: 'common.website' })}
                </FormSection>

                <FormSection title={translate.organization.address}>
                    {renderTextInput({ errors, fieldKey: 'street', label: 'organization.streetName' })}
                    {renderHouseNumberFields()}
                    {renderTextInput({ errors, fieldKey: 'zip', isRequired: true, label: 'common.zipcode' })}
                    {renderTextInput({ errors, fieldKey: 'city', isRequired: true, label: 'common.city' })}

                    <FormGroup
                        hasErrors={errors.has('state')}
                        isRequired={organization.country === 'NL'}
                        label={translate.common.state}
                    >
                        <SelectState
                            country={organization.country}
                            error={getValidationErrorTranslation(errors.get('state'))}
                            onChange={async (state) => handleChangeAndValidate('state', state)}
                            value={organization.state}
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('country')}
                        label={translate.common.country}
                    >
                        <SelectCountry
                            error={errors.get('country')}
                            onChange={async (value) => {
                                handleChangeAndValidate('country', value);
                            }}
                            value={organization.country}
                        />
                    </FormGroup>
                </FormSection>
            </Card.Body>
        </Card>
    );
}
